import { FC, useContext } from 'react'
import { Box, Grid } from '@/components/atoms/Grid'

import useTranslation from 'next-translate/useTranslation'
import { Translate } from 'next-translate'
import { FilterCheckboxRedesign } from '@/components/molecules/filter'
import { useFilter } from '@/providers/filterProvider/filtersProvider'
import { ActiveFilterItem } from 'types/activeFiltersType'
import { FilterSection, PriceRange } from '@/network/graphql.g'
import { ActiveFiltersTypeAliasContext } from '@/providers/filterProvider/filterProviderTypes'
import { StoreContext, useDomain } from '@/providers/storeProvider'
import MobileMenuBoxRedesign, {
  ArrowState
} from '@/components/molecules/mobileMenuBoxRedesign'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { ShippingTimePromoInfoBox } from '@/components/molecules/purchasePromoInfoBox'
import PriceFilterRedesign from '@/components/organisms/filter/priceFilterRedesign'

export enum FilterMainSliderNavigationActionType {
  color,
  pattern,
  material,
  size,
  manufacturer,
  gender,
  category,
  tag,
  price,
  sort
}

type FilterMainSlidePageRedesignProps = {
  priceRange: PriceRange
  showGenders: boolean
  showCategories: boolean
  showSizes: boolean
  showColors: boolean
  showPatterns: boolean
  showMaterials: boolean
  showManufacturers: boolean
  showInStorage: boolean
  showInSale: boolean
  showInStorageCheckbox: boolean
  tags: FilterSection[]
  onActionClick: (
    navigationActionType: FilterMainSliderNavigationActionType,
    navigationSubActionType?: string
  ) => void
} & Rest

const FilterMainSlidePageRedesign: FC<FilterMainSlidePageRedesignProps> = ({
  priceRange,
  showGenders,
  showCategories,
  showSizes,
  showColors,
  showPatterns,
  showMaterials,
  showManufacturers,
  showInStorage,
  showInSale,
  showInStorageCheckbox,
  tags,
  onActionClick,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { activeFilters, filter } = useFilter()
  const { hasInStock, hasInMainWarehouse } = filter || {}
  const { isFrogies, isAliatic } = useContext(StoreContext)
  const { domainName } = useDomain()
  return (
    <Box py={2} {...rest}>
      <Grid>
        {makeMenuBoxes(
          t,
          activeFilters,
          showGenders,
          showCategories,
          showSizes,
          showColors,
          showPatterns,
          showMaterials,
          showManufacturers
        ).map(({ title, subtitle, action }) => (
          <MobileMenuBoxRedesign
            key={title}
            id={title}
            title={title}
            subtitle={subtitle}
            onClick={() => onActionClick(action)}
            arrowState={ArrowState.next}
          />
        ))}
        {showInSale && (
          <FilterCheckboxRedesign filterType={FilterQueryItem.IN_SALE} />
        )}
        {showInStorage && (
          <>
            {isFrogies || isAliatic || !showInStorageCheckbox ? (
              <ShippingTimePromoInfoBox
                display={{ tablet: 'flex', mobile: 'none' }}
                shippingTitle={t(
                  `Category.Filter.promoShippingTime_${domainName}`
                )}
              />
            ) : (
              <>
                {hasInStock && (
                  <FilterCheckboxRedesign filterType={FilterQueryItem.STOCK} />
                )}
                {hasInMainWarehouse && (
                  <FilterCheckboxRedesign
                    filterType={FilterQueryItem.MAIN_WAREHOUSE}
                  />
                )}
              </>
            )}
          </>
        )}
        {priceRange && (
          <PriceFilterRedesign
            py="16px"
            priceRange={priceRange}
            data-testid="filterPrice"
          />
        )}
        {tags &&
          makeTagMenuBoxes(t, tags, activeFilters.tags).map(
            ({ title, subtitle, action, subAction }) => (
              <MobileMenuBoxRedesign
                key={title}
                id={title}
                title={title.toUpperCase()}
                subtitle={subtitle}
                onClick={() => onActionClick(action, subAction)}
                arrowState={ArrowState.next}
                isMain={false}
              />
            )
          )}
      </Grid>
    </Box>
  )
}

export default FilterMainSlidePageRedesign

type MenuBox = {
  action: FilterMainSliderNavigationActionType
  subAction?: string
  title: string
  subtitle?: string
}

const makeMenuBoxes = (
  t: Translate,
  activeFilters: ActiveFiltersTypeAliasContext,
  showGenders: boolean,
  showCategories: boolean,
  showSizes: boolean,
  showColors: boolean,
  showPatterns: boolean,
  showMaterials: boolean,
  showManufacturers: boolean
): MenuBox[] => {
  const menuBoxes: MenuBox[] = []

  showGenders &&
    menuBoxes.push({
      title: t('Category.Filter.gender'),
      subtitle: makeSubtitle(activeFilters.genders),
      action: FilterMainSliderNavigationActionType.gender
    })
  showCategories &&
    menuBoxes.push({
      title: t('Category.Filter.category'),
      subtitle: makeSubtitle(activeFilters.categories),
      action: FilterMainSliderNavigationActionType.category
    })
  showSizes &&
    menuBoxes.push({
      title: t('Category.size'),
      subtitle: makeSubtitle(activeFilters.sizes),
      action: FilterMainSliderNavigationActionType.size
    })
  showColors &&
    menuBoxes.push({
      title: t('Category.Filter.color'),
      subtitle: makeSubtitle(activeFilters.colors),
      action: FilterMainSliderNavigationActionType.color
    })
  showPatterns &&
    menuBoxes.push({
      title: t('Category.Filter.motive'),
      subtitle: makeSubtitle(activeFilters.patterns),
      action: FilterMainSliderNavigationActionType.pattern
    })
  showMaterials &&
    menuBoxes.push({
      title: t('Category.Filter.material'),
      subtitle: makeSubtitle(activeFilters.materials),
      action: FilterMainSliderNavigationActionType.material
    })
  showManufacturers &&
    menuBoxes.push({
      title: t('Category.Filter.manufacturer'),
      subtitle: makeSubtitle(activeFilters.manufacturers),
      action: FilterMainSliderNavigationActionType.manufacturer
    })
  return menuBoxes
}

const makeTagMenuBoxes = (
  t: Translate,
  tags: FilterSection[],
  activeTagFilters: { id: string; items: ActiveFilterItem[] }[]
): MenuBox[] => {
  const activeTags: FilterSection[] = []
  const inactiveTags: FilterSection[] = []

  tags.forEach((tag) => {
    const hasActiveItems =
      activeTagFilters.find(({ id }) => id === tag.id)?.items?.length > 0
    if (hasActiveItems) {
      activeTags.push(tag)
    } else {
      inactiveTags.push(tag)
    }
  })

  return [...activeTags, ...inactiveTags].map((tag) => ({
    title: tag.title,
    subtitle: makeSubtitle(
      activeTagFilters.find(({ id }) => id === tag.id)?.items
    ),
    action: FilterMainSliderNavigationActionType.tag,
    subAction: tag.id
  }))
}

const makeSubtitle = (
  activeFilterItem?: ActiveFilterItem[]
): string | undefined => activeFilterItem?.map(({ title }) => title).join(', ')
